import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';

import { FormDialogShellComponent } from '@shared/components/form-dialog/form-dialog-shell.component';
import { AutoSelectComponent } from '@app/shared/components/auto-select/auto-select.component';
import { CustomMessageService } from '@shared/components/custom-message/custom-message.service';
import { StockService } from '@shared/services/entities/stock.service';
import { RfidDevicesService } from '@shared/services/entities/rfid-devices.service';
import { JetstreamService } from '@shared/services/jetstream.service';
import { MatSelectModule } from '@angular/material/select';
import { LogisticsCatalogs, MaterialRequestsService, TransfersService, TransferRequestsService, errMsg, fmtDate } from '../logistics.service';
import { expiryTone } from '@app/shared/utils/expiry';

export interface TransferPrefill {
  request?: string | null;
  requestFolio?: string | null;
  originWarehouse?: string | null;
  destinationWarehouse?: string | null;
  /** Piezas a apartar por producto (se eligen por caducidad más próxima). */
  wanted?: { product: string; quantity: number }[];
  /** Solicitud de traspaso (SOT) que se atiende: fija origen, destino y lo pedido. */
  transferRequest?: string | null;
}
export interface TransferFormData { transfer?: any; prefill?: TransferPrefill & { transferRequestRecord?: any }; }

/**
 * Alta / edición (en borrador) de un traspaso entre cuentas: origen, destino y
 * las piezas disponibles del origen que se van (escaneando la etiqueta o
 * marcándolas). Al guardar puede enviarse de una vez.
 */
@Component({
  selector: 'app-transfer-form-dialog',
  standalone: true,
  imports: [FormsModule, MatDialogModule, MatFormFieldModule, MatInputModule, MatButtonModule, MatIconModule,
    MatCheckboxModule, MatSlideToggleModule, MatTooltipModule, MatSelectModule, FormDialogShellComponent, AutoSelectComponent],
  styleUrl: '../logistics-dialog.scss',
  styles: [`
    .req { grid-column: 1 / -1; border: 1px solid #DCDCDC; border-radius: 10px; overflow: hidden; }
    .req table { width: 100%; border-collapse: collapse; font-size: 13px; }
    .req th { text-align: left; font-weight: 600; color: #555; padding: 6px 10px; background: #F4F6FD; }
    .req td { padding: 6px 10px; border-top: 1px solid #F0F0F0; }
    .req .num { text-align: right; font-variant-numeric: tabular-nums; }
    .req .ok { color: #14853d; font-weight: 600; } .req .part { color: #9a6b00; font-weight: 600; } .req .over { color: #c62828; font-weight: 600; }
    .rfid { grid-column: 1 / -1; display: flex; flex-wrap: wrap; align-items: center; gap: 8px 12px; }
    .rfid mat-form-field { flex: 1 1 280px; }
    .rfid-result { grid-column: 1 / -1; display: flex; flex-wrap: wrap; gap: 6px 14px; font-size: 13px; }
    .rfid-result b { font-variant-numeric: tabular-nums; }
  `],
  template: `
<app-form-dialog-shell [icon]="isEditing ? 'edit' : 'local_shipping'"
  [heading]="isEditing ? 'Traspaso ' + data.transfer.folio : 'Nuevo traspaso de material'"
  [saving]="saving()" [saveLabel]="sendNow ? 'Guardar y enviar' : 'Guardar borrador'" [saveIcon]="sendNow ? 'send' : 'save'"
  formId="transfer-form" (cancelled)="ref.close(false)">
<form id="transfer-form" (ngSubmit)="save()">
  <div class="form-grid">
    @if (requestFolio) {
      <div class="hint-box"><mat-icon>assignment</mat-icon><span>Surte la solicitud <b>{{ requestFolio }}</b>.</span></div>
    }
    @if (attending) {
      <div class="hint-box"><mat-icon>assignment</mat-icon><span>Atiende la solicitud de traspaso <b>{{ attending.folio }}</b>.</span></div>
    } @else if (!requestFolio) {
      <app-auto-select class="full-col" label="Solicitud de traspaso (opcional)" name="transferRequest" [(ngModel)]="transferRequest"
        (changed)="onTransferRequest()" [options]="trOptions" [labelFn]="trLabel" [allowEmpty]="true" emptyLabel="— Sin solicitud —"
        [disabled]="isEditing && !!data.transfer?.transferRequest"></app-auto-select>
    }
    <app-auto-select [label]="trHospital ? 'Origen (locker de ' + cat.placeName(tr.place) + ')' : 'Origen (locker / in-house)'" name="origin" [(ngModel)]="origin" (changed)="loadPieces()"
      [options]="originOptions" [labelFn]="cat.warehouseLabel" [disabled]="!!tr && !trHospital"></app-auto-select>
    <app-auto-select [label]="trHospital ? 'Destino (locker de ' + cat.placeName(tr.toPlace) + ')' : 'Destino (locker / in-house)'" name="destination" [(ngModel)]="destination" (changed)="loadDevices()"
      [options]="destinationOptions" [labelFn]="cat.warehouseLabel" [disabled]="!!tr && !trHospital"></app-auto-select>

    @if (tr) {
      <div class="section-title"><mat-icon>assignment</mat-icon> Lo que pide {{ tr.folio }}<span class="spacer"></span>
        <span class="counter">Solicita: {{ tr.requestedBy || '—' }}</span></div>
      <div class="req">
        <table>
          <thead><tr><th>Código</th><th>Descripción</th><th class="num">Pedido</th><th class="num">Ya surtido</th><th class="num">Faltan</th><th class="num">En este traspaso</th></tr></thead>
          <tbody>
            @for (l of trLines; track l.product) {
              <tr>
                <td>{{ l.code }}</td><td>{{ l.description }}</td>
                <td class="num">{{ l.quantity }}</td><td class="num">{{ l.supplied }}</td><td class="num">{{ l.missing }}</td>
                <td class="num" [class]="'num ' + toneOf(selectedOf(l.product), l.missing)">{{ selectedOf(l.product) }}</td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    }

    @if (destination) {
      <div class="section-title"><mat-icon>sensors</mat-icon> Lector RFID del destino
        <span class="spacer"></span><span class="counter">Lee lo que entró a {{ cat.warehousesById[destination]?.name }}</span></div>
      <div class="rfid">
        <mat-form-field appearance="outline" subscriptSizing="dynamic">
          <mat-label>Dispositivo</mat-label>
          <mat-select name="device" [(ngModel)]="deviceId">
            @for (d of devices; track d.id) { <mat-option [value]="d.id">{{ d.name }} · {{ deviceType(d) }}</mat-option> }
          </mat-select>
          @if (!devices.length) { <mat-hint>Sin dispositivos para este locker en Tecnología RFID.</mat-hint> }
        </mat-form-field>
        <button mat-flat-button color="primary" type="button" (click)="readDevice()" [disabled]="!deviceId || reading || !origin">
          <mat-icon>{{ reading ? 'hourglass_top' : 'contactless' }}</mat-icon> {{ reading ? 'Leyendo…' : 'Leer lo que entró' }}</button>
      </div>
      @if (rfidRead) {
        <div class="rfid-result">
          <span>Etiquetas leídas: <b>{{ rfidRead.total }}</b></span>
          <span class="ok">Del origen (seleccionadas): <b>{{ rfidRead.fromOrigin }}</b></span>
          <span>Ya estaban en el destino: <b>{{ rfidRead.inDestination }}</b></span>
          @if (rfidRead.notRequested) { <span class="flag">No pedidas: <b>{{ rfidRead.notRequested }}</b></span> }
          @if (rfidRead.unknown) { <span class="flag">Otras / sin registro: <b>{{ rfidRead.unknown }}</b></span> }
        </div>
      }
    }

    @if (origin && destination) {
      <div class="hint-box" [class.hint-box--warn]="!sameHospital">
        <mat-icon>{{ sameHospital ? 'swap_horiz' : 'local_shipping' }}</mat-icon>
        <span>{{ sameHospital
          ? 'Mismo hospital: al enviar, las piezas quedan de inmediato en el destino.'
          : 'Entre hospitales: al enviar, las piezas quedan en tránsito y se avisa a mesa de control para pedir la recolección a DHL.' }}</span>
      </div>
    }

    <div class="section-title">
      <mat-icon>nfc</mat-icon> Piezas del origen
      <span class="spacer"></span>
      <span class="counter">{{ selected.size }} seleccionada(s){{ wantedTotal ? ' · se piden ' + wantedTotal : '' }}</span>
    </div>
    <div class="scan-row">
      <mat-form-field appearance="outline" subscriptSizing="dynamic">
        <mat-label>Escanear etiqueta RFID</mat-label>
        <mat-icon matPrefix>qr_code_scanner</mat-icon>
        <input matInput name="scan" [(ngModel)]="scan" autocomplete="off" maxlength="40"
          (keydown.enter)="$event.preventDefault(); onScan()">
      </mat-form-field>
      <mat-form-field appearance="outline" subscriptSizing="dynamic">
        <mat-label>Filtrar</mat-label>
        <mat-icon matPrefix>search</mat-icon>
        <input matInput name="filter" [(ngModel)]="filter" autocomplete="off" placeholder="Código, descripción, lote…">
      </mat-form-field>
    </div>
    @if (scanMsg) { <div class="hint-box hint-box--danger"><mat-icon>error</mat-icon><span>{{ scanMsg }}</span></div> }

    <div class="pieces">
      <table>
        <thead><tr>
          <th><mat-checkbox color="primary" [checked]="allVisibleOn" (change)="toggleVisible($event.checked)"></mat-checkbox></th>
          <th>Código</th><th>Descripción</th><th>Lote</th><th>Caducidad</th><th>Etiqueta</th>
        </tr></thead>
        <tbody>
          @for (p of visible; track p.id) {
            <tr [class.is-on]="selected.has(p.id)">
              <td><mat-checkbox color="primary" [checked]="selected.has(p.id)" (change)="toggle(p.id)"></mat-checkbox></td>
              <td>{{ p._code }}</td><td>{{ p._description }}</td><td>{{ p.batch || '—' }}</td>
              <td [class.flag]="p._tone === 'danger'">{{ p._exp }}</td>
              <td class="tag">{{ p.tagRfid || '—' }}</td>
            </tr>
          }
          @if (!visible.length) {
            <tr><td colspan="6" class="empty">{{ !origin ? 'Elige el origen para ver sus piezas disponibles.' : loadingPieces ? 'Cargando…' : 'Sin piezas disponibles.' }}</td></tr>
          }
        </tbody>
      </table>
    </div>

    <mat-form-field appearance="outline" class="full-col">
      <mat-label>Notas</mat-label>
      <textarea matInput rows="2" name="notes" [(ngModel)]="notes" maxlength="1000"></textarea>
    </mat-form-field>
    <mat-slide-toggle class="full-col" color="primary" name="sendNow" [(ngModel)]="sendNow">Enviar en cuanto se guarde</mat-slide-toggle>
  </div>
</form>
</app-form-dialog-shell>
  `,
})
export class TransferFormDialogComponent implements OnInit {
  readonly data = inject<TransferFormData>(MAT_DIALOG_DATA);
  readonly ref = inject(MatDialogRef<TransferFormDialogComponent>);
  readonly cat = inject(LogisticsCatalogs);
  private readonly service = inject(TransfersService);
  private readonly requests = inject(MaterialRequestsService);
  private readonly stockService = inject(StockService);
  private readonly msg = inject(CustomMessageService);

  readonly saving = signal(false);
  readonly isEditing = !!this.data.transfer;
  readonly requestFolio = this.data.prefill?.requestFolio ?? null;
  private readonly trService = inject(TransferRequestsService);
  private readonly rfidDevices = inject(RfidDevicesService);
  private readonly jetstream = inject(JetstreamService);
  transferRequest: string | null = this.data.transfer?.transferRequest ?? this.data.prefill?.transferRequest ?? null;
  tr: any = null;
  trOptions: any[] = [];
  trLines: { product: string; code: string; description: string; quantity: number; supplied: number; missing: number }[] = [];
  readonly trLabel = (r: any): string => !r ? '' : r.fromWarehouse
    ? `${r.folio} · ${this.cat.placeName(r.place)} · ${this.cat.warehousesById[r.fromWarehouse]?.name ?? ''} → ${this.cat.warehousesById[r.toWarehouse]?.name ?? ''}`
    : `${r.folio} · ${this.cat.placeName(r.place)} → ${this.cat.placeName(r.toPlace)}`;
  devices: any[] = [];
  deviceId: string | null = null;
  reading = false;
  rfidRead: { total: number; fromOrigin: number; inDestination: number; notRequested: number; unknown: number } | null = null;
  origin: string | null = this.data.transfer?.originWarehouse ?? this.data.prefill?.originWarehouse ?? null;
  destination: string | null = this.data.transfer?.destinationWarehouse ?? this.data.prefill?.destinationWarehouse ?? null;
  notes = this.data.transfer?.notes ?? '';
  sendNow = true;
  scan = '';
  scanMsg = '';
  filter = '';
  loadingPieces = false;
  pieces: any[] = [];
  selected = new Set<string>((this.data.transfer?.items ?? []).map((i: any) => i.stock).filter(Boolean));
  private wanted = this.data.prefill?.wanted ?? [];
  readonly wantedTotal = this.wanted.reduce((a, w) => a + (w.quantity || 0), 0);

  get sameHospital(): boolean {
    const o = this.cat.warehousesById[this.origin ?? '']; const d = this.cat.warehousesById[this.destination ?? ''];
    return !!o && !!d && o.place === d.place;
  }

  get visible(): any[] {
    const q = this.filter.trim().toLowerCase();
    return q ? this.pieces.filter(p => p._search.includes(q)) : this.pieces;
  }
  get allVisibleOn(): boolean { const v = this.visible; return v.length > 0 && v.every(p => this.selected.has(p.id)); }

  /** Abierto con "Atender" desde Solicitud de traspaso: la solicitud queda fija. */
  readonly attending: any = this.data.prefill?.transferRequestRecord ?? null;

  ngOnInit(): void {
    if (this.attending) {
      this.transferRequest = this.attending.id;
      this.trOptions = [this.attending];
      this.applyTransferRequest(this.attending, true);
    }
    void this.loadTransferRequests();
    if (this.origin) void this.loadPieces(true);
    if (this.destination) void this.loadDevices();
  }

  // ── Solicitud de traspaso ──────────────────────────────────────────────
  private async loadTransferRequests(): Promise<void> {
    try {
      const r: any = await this.trService.search({ searchType: 'default', condition: { deleted: false } });
      const all = (r?.data ?? []) as any[];
      this.trOptions = all.filter(x => ['Nueva', 'En proceso'].includes(x.status) || x.id === this.transferRequest)
        .sort((a, b) => String(a.folio).localeCompare(String(b.folio), undefined, { numeric: true }));
      if (this.transferRequest) {
        const fresh = all.find(x => x.id === this.transferRequest) ?? this.attending ?? null;
        if (this.attending && fresh && !this.trOptions.some(x => x.id === fresh.id)) this.trOptions = [fresh, ...this.trOptions];
        this.applyTransferRequest(fresh, !this.isEditing && !this.attending);
      }
    } catch { this.trOptions = []; }
  }

  onTransferRequest(): void {
    this.applyTransferRequest(this.trOptions.find(x => x.id === this.transferRequest) ?? null, true);
  }

  /** Solicitud Hospital → Hospital (sin lockers): se eligen aquí. */
  get trHospital(): boolean { return !!this.tr && this.tr.direction === 'HOSPITAL_TO_HOSPITAL' && !this.tr.fromWarehouse; }
  get originOptions(): any[] {
    return this.trHospital ? this.cat.warehouses.filter(w => w.place === this.tr.place && !this.cat.isInhouse(w)) : this.cat.warehouses;
  }
  get destinationOptions(): any[] {
    return this.trHospital ? this.cat.warehouses.filter(w => w.place === this.tr.toPlace && !this.cat.isInhouse(w)) : this.cat.warehouses;
  }

  /** Fija origen y destino de la solicitud y muestra lo que falta por surtir. */
  private applyTransferRequest(r: any, setRoute: boolean): void {
    this.tr = r;
    if (r && r.direction === 'HOSPITAL_TO_HOSPITAL' && !r.fromWarehouse) {
      // Hospital → Hospital: un solo locker de cada lado se elige solo.
      if (setRoute) {
        const o = this.originOptions, d = this.destinationOptions;
        if (!o.some(w => w.id === this.origin)) this.origin = o.length === 1 ? o[0].id : null;
        if (!d.some(w => w.id === this.destination)) this.destination = d.length === 1 ? d[0].id : null;
        if (this.origin) void this.loadPieces();
        if (this.destination) void this.loadDevices();
      }
      this.trLines = (r?.lines ?? []).map((l: any) => ({
        product: l.product, code: l.code, description: l.description, quantity: Number(l.quantity) || 0,
        supplied: Number(l.supplied) || 0, missing: Math.max(0, (Number(l.quantity) || 0) - (Number(l.supplied) || 0)),
      }));
      return;
    }
    this.trLines = (r?.lines ?? []).map((l: any) => ({
      product: l.product, code: l.code, description: l.description, quantity: Number(l.quantity) || 0,
      supplied: Number(l.supplied) || 0, missing: Math.max(0, (Number(l.quantity) || 0) - (Number(l.supplied) || 0)),
    }));
    if (r && setRoute) {
      const changed = this.origin !== r.fromWarehouse;
      this.origin = r.fromWarehouse;
      this.destination = r.toWarehouse;
      if (changed) void this.loadPieces();
      void this.loadDevices();
    }
  }

  selectedOf(product: string): number { return this.pieces.filter(p => p.product === product && this.selected.has(p.id)).length; }
  toneOf(n: number, missing: number): string { return n > missing ? 'over' : n === missing && n > 0 ? 'ok' : n > 0 ? 'part' : ''; }

  // ── Lector RFID del destino ────────────────────────────────────────────
  async loadDevices(): Promise<void> {
    this.devices = []; this.deviceId = null; this.rfidRead = null;
    const dest = this.cat.warehousesById[this.destination ?? ''];
    if (!dest) return;
    try {
      const r: any = await this.rfidDevices.search({ searchType: 'default', condition: { deleted: false, place: dest.place } });
      // Los del locker destino primero; los widgets (se mueven entre lockers) también sirven.
      this.devices = ((r?.data ?? []) as any[])
        .filter(d => d.active !== false && (d.warehouse === dest.id || String(d.type ?? '').startsWith('WIDGET')))
        .sort((a, b) => Number(b.warehouse === dest.id) - Number(a.warehouse === dest.id) || String(a.name).localeCompare(String(b.name)));
      this.deviceId = this.devices[0]?.id ?? null;
    } catch { this.devices = []; }
  }

  deviceType(d: any): string {
    return ({ WIDGET_PC: 'Widget computadora', WIDGET_CELULAR: 'Widget celular', BANDEJA_LECTORA: 'Bandeja lectora',
              ANTENA_LECTORA: 'Antenas lectoras', ANAQUEL_INTELIGENTE: 'Anaquel inteligente' } as any)[d?.type] ?? (d?.type || '');
  }

  /** EPCs de las distintas formas de respuesta de Jetstream. */
  private extractEpcs(payload: any): string[] {
    const out = new Set<string>();
    const walk = (node: any): void => {
      if (!node) return;
      if (typeof node === 'string') { if (node.trim()) out.add(node.trim().toUpperCase()); return; }
      if (Array.isArray(node)) { node.forEach(walk); return; }
      if (typeof node === 'object') {
        for (const k of ['EPCList', 'EpcList', 'epclist', 'EPCs', 'Epcs', 'Tags', 'TagList']) if (node[k]) walk(node[k]);
        for (const k of ['EPC', 'Epc', 'epc', 'TagId', 'Value']) if (typeof node[k] === 'string') walk(node[k]);
        if (node.Events) walk(node.Events);
      }
    };
    walk(payload);
    return [...out];
  }

  /**
   * Lee el dispositivo del destino: las etiquetas que ve y que siguen
   * registradas en el ORIGEN son las piezas que se movieron → se seleccionan.
   */
  async readDevice(): Promise<void> {
    const dev = this.devices.find(d => d.id === this.deviceId);
    if (!dev || !this.origin) return;
    this.reading = true;
    this.rfidRead = null;
    try {
      const name = String(dev.serialNumber || dev.name || '').trim();
      const payload = String(dev.type ?? '').startsWith('WIDGET') ? await this.jetstream.scanning(name) : await this.jetstream.scan(name);
      const epcs = this.extractEpcs(payload);
      if (!epcs.length) {
        await this.msg.open({ title: 'Sin lecturas', type: 'neutral', message: 'El dispositivo no reportó etiquetas.', dismissBtn: true });
        return;
      }
      const byTag = new Map(this.pieces.filter(p => p.tagRfid).map(p => [String(p.tagRfid).toUpperCase(), p]));
      const requested = new Set(this.trLines.map(l => l.product));
      let fromOrigin = 0, notRequested = 0;
      const rest: string[] = [];
      for (const epc of epcs) {
        const p = byTag.get(epc);
        if (!p) { rest.push(epc); continue; }
        fromOrigin++;
        this.selected.add(p.id);
        if (this.tr && !requested.has(p.product)) notRequested++;
      }
      let inDestination = 0;
      if (rest.length && this.destination) {
        const r: any = await this.stockService.search({ searchType: 'default', condition: { deleted: false, warehouse: this.destination, tagRfid: rest } });
        inDestination = ((r?.data ?? []) as any[]).length;
      }
      this.rfidRead = { total: epcs.length, fromOrigin, inDestination, notRequested, unknown: rest.length - inDestination };
    } catch (err) {
      await this.msg.open({ title: 'Error', type: 'error', message: errMsg(err, 'No se pudo leer el dispositivo.'), dismissBtn: true });
    } finally {
      this.reading = false;
    }
  }

  async loadPieces(initial = false): Promise<void> {
    if (!initial) this.selected.clear();
    this.pieces = [];
    if (!this.origin) return;
    this.loadingPieces = true;
    try {
      const r: any = await this.stockService.search({ searchType: 'default', condition: { deleted: false, warehouse: this.origin, status: 'Disponible' } });
      this.pieces = ((r?.data ?? []) as any[]).map(s => {
        const p = this.cat.productsById[s.product] ?? {};
        return { ...s, _code: p.code ?? '', _description: p.description ?? '', _exp: fmtDate(s.expiration, false) || '—',
                 _tone: expiryTone(s.expiration), _search: [p.code, p.description, s.batch, s.tagRfid].join(' ').toLowerCase() };
      }).sort((a, b) => String(a._code).localeCompare(String(b._code)) || String(a.expiration ?? '9999').localeCompare(String(b.expiration ?? '9999')));
      // Desde una solicitud: se apartan las de caducidad más próxima (FEFO).
      if (initial && this.wanted.length && !this.isEditing) {
        for (const w of this.wanted) {
          this.pieces.filter(p => p.product === w.product).slice(0, w.quantity).forEach(p => this.selected.add(p.id));
        }
      }
    } catch (err) {
      await this.msg.open({ title: 'Error', type: 'error', message: errMsg(err, 'No se pudieron cargar las piezas del origen.'), dismissBtn: true });
    } finally {
      this.loadingPieces = false;
    }
  }

  toggle(id: string): void { this.selected.has(id) ? this.selected.delete(id) : this.selected.add(id); }
  toggleVisible(on: boolean): void { this.visible.forEach(p => on ? this.selected.add(p.id) : this.selected.delete(p.id)); }

  onScan(): void {
    const tag = this.scan.trim().toUpperCase();
    this.scan = '';
    if (!tag) return;
    const p = this.pieces.find(x => String(x.tagRfid ?? '').toUpperCase() === tag);
    this.scanMsg = p ? '' : `La etiqueta ${tag} no está disponible en el origen elegido.`;
    if (p) this.selected.add(p.id);
  }

  async save(): Promise<void> {
    const problem = !this.origin || !this.destination ? 'Elige el origen y el destino.'
      : this.origin === this.destination ? 'El origen y el destino no pueden ser el mismo locker.'
      : !this.selected.size ? 'Selecciona al menos una pieza.'
      : this.trHospital
        // Hospital → Hospital: cualquier locker del hospital origen a uno del destino.
        ? (this.cat.warehousesById[this.origin]?.place !== this.tr.place || this.cat.warehousesById[this.destination]?.place !== this.tr.toPlace
            ? `El traspaso debe ir de un locker de ${this.cat.placeName(this.tr.place)} a uno de ${this.cat.placeName(this.tr.toPlace)} según la solicitud ${this.tr.folio}.` : null)
      : this.tr && (this.origin !== this.tr.fromWarehouse || this.destination !== this.tr.toWarehouse)
        ? `El traspaso debe ir de donde sale a donde entra según la solicitud ${this.tr.folio}.` : null;
    if (problem) { await this.msg.open({ title: 'Revisa el traspaso', type: 'warning', message: problem, dismissBtn: true }); return; }

    const items = this.pieces.filter(p => this.selected.has(p.id)).map(p => ({
      stock: p.id, product: p.product, code: p._code, description: p._description,
      batch: p.batch ?? null, expiration: p.expiration ?? null, tagRfid: p.tagRfid ?? null,
    }));
    const o = this.cat.warehousesById[this.origin!]; const d = this.cat.warehousesById[this.destination!];
    const payload: any = {
      originWarehouse: this.origin, originPlace: o?.place ?? null,
      destinationWarehouse: this.destination, destinationPlace: d?.place ?? null,
      items, notes: this.notes.trim() || null,
    };
    this.saving.set(true);
    let id = this.data.transfer?.id ?? null;
    let folio = this.data.transfer?.folio ?? '';
    try {
      if (this.isEditing) {
        await this.service.update({ ...payload, id, folio, status: this.data.transfer.status, request: this.data.transfer.request ?? null,
          transferRequest: this.data.transfer.transferRequest ?? null, operationType: 'single' });
      } else {
        const r: any = await this.service.create({ ...payload, request: this.data.prefill?.request ?? null, transferRequest: this.transferRequest || null });
        id = r?.data?.[0]?.id; folio = r?.data?.[0]?.folio ?? '';
        if (this.data.prefill?.request) await this.markRequestInProgress(this.data.prefill.request);
      }
    } catch (err) {
      this.saving.set(false);
      await this.msg.open({ title: 'Error', type: 'error', message: errMsg(err, 'No se pudo guardar el traspaso.'), dismissBtn: true });
      return;
    }
    try {
      if (this.sendNow && id) {
        const r = await this.service.ship(id);
        const done = r?.status === 'Recibido';
        await this.msg.open({ title: done ? '¡Traspaso hecho!' : '¡Enviado!', type: 'success', duration: 3000,
          message: done ? `Traspaso <b>${folio}</b>: las piezas ya están en el destino.`
                        : `Traspaso <b>${folio}</b> listo para recolección. Se avisó a mesa de control para pedir la recolección a DHL.` });
      } else {
        await this.msg.open({ title: '¡Guardado!', type: 'success', message: `Traspaso <b>${folio}</b> guardado como borrador.`, duration: 2200 });
      }
      this.ref.close(true);
    } catch (err) {
      await this.msg.open({ title: 'Guardado, pero no se envió', type: 'warning', dismissBtn: true,
        message: `El traspaso <b>${folio}</b> quedó en borrador: ${errMsg(err, 'no se pudo enviar.')}` });
      this.ref.close(true);
    } finally {
      this.saving.set(false);
    }
  }

  /** La solicitud pasa a "En surtido" en cuanto tiene un traspaso. */
  private async markRequestInProgress(requestId: string): Promise<void> {
    try {
      const r: any = await this.requests.search({ searchType: 'default', condition: { id: requestId } });
      const req = r?.data?.[0];
      if (req && req.status === 'Nueva') await this.requests.update({ ...req, status: 'En surtido', operationType: 'single' });
    } catch { /* no bloquea el traspaso */ }
  }
}
